import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Users, Search, Edit, Trash2, Shield, RefreshCw, Store, Link } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const roles = [
  { value: 'customer', label: 'Customer' },
  { value: 'driver', label: 'Driver' },
  { value: 'restaurant_admin', label: 'Restaurant Admin' },
  { value: 'sales_person', label: 'Sales Person' },
  { value: 'super_admin', label: 'Super Admin' },
];

const roleColors = {
  customer: 'bg-blue-100 text-blue-700',
  driver: 'bg-green-100 text-green-700',
  restaurant_admin: 'bg-purple-100 text-purple-700',
  sales_person: 'bg-orange-100 text-orange-700',
  super_admin: 'bg-red-100 text-red-700',
};

export default function UserManagement() {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [editUser, setEditUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [assignDialog, setAssignDialog] = useState({ open: false, user: null });
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchRestaurants();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRestaurants = async () => {
    try {
      const response = await api.get('/restaurants');
      setRestaurants(response.data);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    }
  };

  const updateUserRole = async () => {
    if (!editUser || !newRole) return;

    try {
      await api.put(`/users/${editUser.id}/role`, { role: newRole });
      toast.success('User role updated');
      setEditUser(null);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  const assignRestaurant = async () => {
    if (!assignDialog.user || !selectedRestaurantId) {
      toast.error('Please select a restaurant');
      return;
    }

    try {
      await api.put(`/users/${assignDialog.user.id}/transfer-restaurant`, { 
        restaurant_id: selectedRestaurantId 
      });
      const restaurant = restaurants.find(r => r.id === selectedRestaurantId);
      toast.success(`Restaurant "${restaurant?.name}" assigned to ${assignDialog.user.email}`);
      setAssignDialog({ open: false, user: null });
      setSelectedRestaurantId('');
      fetchUsers();
      fetchRestaurants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign restaurant');
    }
  };

  const deleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user?')) return;

    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Get restaurant name for a user
  const getUserRestaurant = (user) => {
    if (user.role !== 'restaurant_admin') return null;
    const restaurant = restaurants.find(r => r.owner_id === user.id);
    return restaurant?.name || null;
  };

  return (
    <div className="space-y-6" data-testid="user-management">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
        <Button variant="outline" onClick={fetchUsers}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search users..."
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {roles.map((role) => (
              <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Name</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Email</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Role</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Restaurant</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td colSpan={6} className="text-center py-8">
                    <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                  </td>
                </tr>
              ) : filteredUsers.length > 0 ? (
                filteredUsers.map((user) => {
                  const restaurantName = getUserRestaurant(user);
                  return (
                    <tr key={user.id} className="hover:bg-slate-50" data-testid={`user-row-${user.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{user.name}</td>
                      <td className="px-4 py-3 text-slate-600">{user.email}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleColors[user.role]}`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {user.role === 'restaurant_admin' ? (
                          restaurantName ? (
                            <span className="text-sm text-green-600 flex items-center gap-1">
                              <Store className="w-3 h-3" />
                              {restaurantName}
                            </span>
                          ) : (
                            <span className="text-sm text-orange-500 italic">Not assigned</span>
                          )
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${user.is_verified ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {user.is_verified ? 'Verified' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => { setEditUser(user); setNewRole(user.role); }}
                            data-testid={`edit-user-${user.id}`}
                            title="Change Role"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          {user.role === 'restaurant_admin' && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setAssignDialog({ open: true, user });
                                setSelectedRestaurantId('');
                              }}
                              data-testid={`assign-restaurant-${user.id}`}
                              title="Assign Restaurant"
                              className="text-purple-500 hover:text-purple-700"
                            >
                              <Link className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-700"
                            onClick={() => deleteUser(user.id)}
                            data-testid={`delete-user-${user.id}`}
                            title="Delete User"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-slate-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
          <Store className="w-4 h-4" />
          Restaurant Admin Onboarding
        </h3>
        <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
          <li>Restaurant admins can self-register on the website</li>
          <li>Use the <strong>link icon</strong> (🔗) to assign a restaurant to a restaurant admin</li>
          <li>Once assigned, the admin can manage their restaurant's menu, tables, waiters, etc.</li>
          <li>An admin can only manage restaurants assigned to them</li>
        </ul>
      </div>

      {/* Edit Role Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-slate-500">User</p>
              <p className="font-medium">{editUser?.name} ({editUser?.email})</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 mb-2">New Role</p>
              <Select value={newRole} onValueChange={setNewRole}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
              <Button onClick={updateUserRole}>Update Role</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Restaurant Dialog */}
      <Dialog open={assignDialog.open} onOpenChange={(open) => !open && setAssignDialog({ open: false, user: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Restaurant</DialogTitle>
            <DialogDescription>
              Select a restaurant to assign to {assignDialog.user?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>User</Label>
              <p className="font-medium text-slate-900">{assignDialog.user?.name}</p>
              <p className="text-sm text-slate-500">{assignDialog.user?.email}</p>
            </div>
            <div>
              <Label>Select Restaurant</Label>
              <Select value={selectedRestaurantId} onValueChange={setSelectedRestaurantId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a restaurant..." />
                </SelectTrigger>
                <SelectContent>
                  {restaurants.map((restaurant) => (
                    <SelectItem key={restaurant.id} value={restaurant.id}>
                      <div className="flex items-center gap-2">
                        <Store className="w-4 h-4 text-slate-400" />
                        <span>{restaurant.name}</span>
                        {restaurant.owner_id && (
                          <span className="text-xs text-slate-400">(has owner)</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                This will set the user as the owner of this restaurant
              </p>
            </div>
            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={() => setAssignDialog({ open: false, user: null })}>
                Cancel
              </Button>
              <Button onClick={assignRestaurant} disabled={!selectedRestaurantId}>
                <Link className="w-4 h-4 mr-2" />
                Assign Restaurant
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
